/**
 * BaaS Pix via MobileService `/v1/ib/console/*` → `/v1/pix/accounts/{accountId}/...`
 */

import { baasProxy, resolveBaasProxyContext } from "./baasConsoleClient";

const pixBase = (accountId) =>
  `v1/pix/accounts/${encodeURIComponent(accountId)}`;

export const listPixKeys = async (instance, accountId) => {
  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: `${pixBase(accountId)}/keys`,
  });
  return Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
};

export const createPixKey = async (instance, accountId, body = { key_type: "EVP" }) =>
  baasProxy({
    instance,
    method: "POST",
    baasPath: `${pixBase(accountId)}/keys`,
    body,
  });

export const deletePixKey = async (instance, accountId, keyId) =>
  baasProxy({
    instance,
    method: "DELETE",
    baasPath: `${pixBase(accountId)}/keys/${encodeURIComponent(keyId)}`,
  });

/** Link an owned Pix key to OPERATIONAL / WALLET / OTC / ORIGIN_RAIL for Contas listing. */
export const linkPixKey = async (instance, originAccountId, keyId, linkedAccountId) =>
  baasProxy({
    instance,
    method: "POST",
    baasPath: `${pixBase(originAccountId)}/keys/${encodeURIComponent(keyId)}/link`,
    body: { linked_account_id: linkedAccountId },
  });

/** Deactivate the active link (path account must be the key origin / PaySmart owner). */
export const unlinkPixKey = async (instance, originAccountId, keyId) =>
  baasProxy({
    instance,
    method: "DELETE",
    baasPath: `${pixBase(originAccountId)}/keys/${encodeURIComponent(keyId)}/link`,
  });

export const consultPixKey = async (instance, accountId, key) =>
  baasProxy({
    instance,
    method: "GET",
    // Query ?key= avoids Azure/IIS HTML 404 on path segments with + / %2B (phone E.164).
    // Axios params encode '+' as %2B; Baas decodes and sends literal '+' to PaySmart.
    baasPath: `${pixBase(accountId)}/keys/consult`,
    query: { key },
  });

export const listPixPayments = async (instance, accountId, query = {}) => {
  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: `${pixBase(accountId)}/payments`,
    query,
  });
  return {
    items: Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [],
    total: Number(data?.total ?? 0),
    raw: data,
  };
};

export const payPixByKey = async (instance, accountId, body) =>
  baasProxy({
    instance,
    method: "POST",
    baasPath: `${pixBase(accountId)}/payments/key`,
    body,
  });

export const createPixChargeback = async (instance, accountId, body) =>
  baasProxy({
    instance,
    method: "POST",
    baasPath: `${pixBase(accountId)}/chargebacks`,
    body,
  });

export const payPixCopyPaste = async (instance, accountId, body) =>
  baasProxy({
    instance,
    method: "POST",
    baasPath: `${pixBase(accountId)}/payments/send-copy-paste`,
    body,
  });

export const getPixLimits = async (instance, accountId, { resolved = true } = {}) =>
  baasProxy({
    instance,
    method: "GET",
    baasPath: `${pixBase(accountId)}/limits`,
    query: { resolved },
  });

/** Used / available / max per category — `GET …/limits/available`. */
export const getPixAvailableLimits = async (instance, accountId) => {
  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: `${pixBase(accountId)}/limits/available`,
  });
  return Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
};

export const setPixLimits = async (instance, accountId, body) =>
  baasProxy({
    instance,
    method: "PUT",
    baasPath: `${pixBase(accountId)}/limits`,
    body,
  });

export const getEntityPixLimits = async (instance, entityId, { resolved = true } = {}) =>
  baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/pix/limits`,
    query: { resolved },
  });

/** Instance ceiling policies — `GET /v1/instances/{id}/pix/limits`. */
export const getInstancePixLimits = async (instance) => {
  const { instanceId } = resolveBaasProxyContext(instance);
  return baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/instances/${instanceId}/pix/limits`,
  });
};

/** Update instance Pix ceilings — `PUT /v1/instances/{id}/pix/limits`. */
export const setInstancePixLimits = async (instance, body) => {
  const { instanceId } = resolveBaasProxyContext(instance);
  return baasProxy({
    instance,
    method: "PUT",
    baasPath: `v1/instances/${instanceId}/pix/limits`,
    body,
  });
};
